import { Channel } from '@/Enums/Channel.enum';
import { Route } from '@/Enums/Routes/Route.enum';
import { ScreenKey } from '@/Enums/Screens/ScreenKey.enum';
import AIButton from '@/components/Buttons/AIButton';
import OptionsIcon from '@/components/Icons/OptionsIcon';
import {
  FORECASTER_CALCULATOR_FIELDS,
  FORECASTER_UPDATE_ACTIONS,
  MENU_ID,
  MENU_TRIGGER_ID,
  isGamChannel,
} from '@/constants/forecaster';
import { HEADER_HEIGHT } from '@/constants/layout';
import { mapDataToSend, mapValueForChannel } from '@/helpers/forecaster';
import { getCustomFlightDates } from '@/helpers/forecaster/estimation';
import { getDocumentTitle } from '@/helpers/printing';
import { isRouteAccessibile } from '@/helpers/routes';
import ForecasterHeader from '@/pages/Forecaster/styled/ForecasterHeader';
import { useAppDispatch, useAppSelector } from '@/store/hooks/index';
import { selectIsAiForecasterHidden } from '@/store/selectors/currentWhitelabel.selectors';
import { selectForecasterChannelIds } from '@/store/selectors/forecaster.selectors';
import {
  getAudience,
  getCurrentForecast,
  getForecastChannelsData,
  getForecastGeoImpressions,
  getProducts,
  postForecast,
  resetCurrentForecast,
} from '@/store/slices/forecasterSlice';
import { getDealGroups, getPackages } from '@/store/slices/sharedApi.slice';
import { ArrowDropDown, SaveOutlined } from '@mui/icons-material';
import { Backdrop, Box, CircularProgress, Menu, MenuItem } from '@mui/material';
import Button from '@mui/material/Button';
import debounce from 'debounce';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import Channels from './Channels';
import ControlComponents from './ControlComponents';
import Estimation from './Estimation';
import SpendCalculator from './Estimation/components/SpendCalculator';
import ForecastName from './ForecastName';
import Geographic from './Geographic';

const Forecaster = () => {
  //#region hooks, selectors
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const printContainerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams({
    currentChannel: Channel.CTV,
  });

  const currentChannels = useAppSelector(selectForecasterChannelIds);

  const { screens } = useAppSelector(state => state.me);
  const isAiForecasterHidden = useAppSelector(selectIsAiForecasterHidden);
  const forecasterData = useAppSelector(state => state.forecaster);
  const {
    channelsData,
    forecastRequestId,
    currentForecast,
    currentForecastRequestId,
    productsRequestId,
    audienceRequestId,
  } = forecasterData;
  //#endregion

  //#region form
  const methods = useForm({
    defaultValues: useMemo(() => currentForecast, [currentForecast]),
  });

  const { getValues, watch, reset, control, handleSubmit, setValue } = methods;
  const currentChannelWatch = useWatch({ control, name: 'currentChannel' });
  //#endregion

  //#region useState
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [currentChannelControlsLocked, setCurrentChannelControlsLocked] = useState(true);
  //#endregion

  //#region useMemo
  const menuOpen = useMemo(() => Boolean(menuAnchorEl), [menuAnchorEl]);

  const isAIAllowed = useMemo(
    () => !isAiForecasterHidden && isRouteAccessibile(screens, ScreenKey.FORECASTER_AI),
    [screens, isAiForecasterHidden],
  );
  //#endregion

  //#region useCallback
  const onMenuOpen = useCallback(event => {
    setMenuAnchorEl(event.currentTarget);
  }, []);
  const onMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const navigateToForecasterTable = useCallback(() => navigate(Route.FORECASTER_TABLE), [navigate]);
  const navigateToForecasterAI = useCallback(() => navigate(Route.FORECASTER_AI), [navigate]);

  const fetchForecast = useCallback(
    payload => {
      dispatch(getForecastChannelsData(payload));
    },
    [dispatch],
  );

  const debouncedFetch = useMemo(() => {
    return debounce(fetchForecast, 800);
  }, [fetchForecast]);

  const updateChannel = useCallback(
    (value, skipLoading = false) => {
      // Don't dispatch until channels resolve; normalize an unavailable channel so we never forecast a gated one.
      if (!currentChannels.length) return;
      // daysInRange is required by preview-channels — an empty/not-yet-loaded forecast
      // (e.g. a failed ?id= load) would otherwise send it and get a 422 (flank#5174).
      if (!value.daysInRange) return;
      const requested = value.currentChannel;
      const channelName = currentChannels.includes(requested) ? requested : currentChannels[0];
      const filteredChannels = [mapValueForChannel(getValues(channelName))];
      if (!skipLoading) {
        setCurrentChannelControlsLocked(true);
      }
      debouncedFetch({
        filteredChannels,
        frequencyCapPerDay: value.frequencyCapEnabled ? value.frequencyCapPerDay : null,
        geographic: value.geographic,
        daysInRange: value.daysInRange,
        ...getCustomFlightDates(value.tabIndex, value.dateRange),
        // GAM has no radius targeting — never send a carried-over geoFence.
        geoFence: isGamChannel(channelName) ? null : value.geoFence,
        geoExclusion: value.geoExclusion,
        onSuccess: () => setCurrentChannelControlsLocked(false),
        onReject: () => setCurrentChannelControlsLocked(false),
      });
    },
    [getValues, debouncedFetch, currentChannels],
  );

  const getGeoImpressions = useCallback(() => {
    const value = getValues();
    const channelName = value.currentChannel;
    const filteredChannel = mapValueForChannel(getValues(channelName));
    const geographic = getValues('geographic');

    if (!geographic?.length) return;

    const body = {
      ...filteredChannel,
      name: channelName,
      daysInRange: getValues('daysInRange'),
      geographic,
    };

    dispatch(getForecastGeoImpressions(body));
  }, [getValues, dispatch]);

  const debouncedGeoImpressions = useMemo(() => {
    return debounce(() => {
      getGeoImpressions();
    }, 500);
  }, [getGeoImpressions]);
  //#endregion

  //#region useEffect
  useEffect(() => {
    dispatch(getProducts());
    dispatch(getAudience());
    dispatch(getDealGroups());
    dispatch(getPackages());
    return () => {
      dispatch(resetCurrentForecast());
    };
  }, [dispatch]);

  useEffect(() => {
    if (productsRequestId || audienceRequestId) return;
    const searchParamsToSet = { currentChannel: searchParams.get('currentChannel') };
    const searchId = searchParams.get('id');
    if (searchId) {
      searchParamsToSet.id = searchId;
    }
    dispatch(getCurrentForecast(searchParamsToSet));
  }, [dispatch, productsRequestId, audienceRequestId]);
  // currentForecastRequestId starts at the DEFAULT_LOADING_REQUEST_ID sentinel
  // (truthy), so this doesn't fire on a cold mount — but resetCurrentForecast
  // (on unmount) clears currentForecast without restoring that sentinel, so
  // returning to Forecaster after leaving it re-fires this effect with the
  // still-empty currentForecast (flank#5174). This ref is per-mount, so it
  // starts false again on every re-entry and blocks that stale-empty run.
  const hasRequestedCurrentForecast = useRef(false);
  useEffect(() => {
    if (currentForecastRequestId) {
      hasRequestedCurrentForecast.current = true;
    }
  }, [currentForecastRequestId]);

  useEffect(() => {
    if (!currentForecastRequestId && hasRequestedCurrentForecast.current) {
      reset(currentForecast);
      updateChannel(currentForecast);
    }
  }, [reset, currentForecast, currentForecastRequestId, updateChannel]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (FORECASTER_UPDATE_ACTIONS.includes(name)) {
        updateChannel(value);
      } else if (name?.startsWith(`${value.currentChannel}.`)) {
        updateChannel(value, true);
      }
      if (!FORECASTER_CALCULATOR_FIELDS.includes(name)) {
        debouncedGeoImpressions();
      }
    });
    return () => {
      subscription.unsubscribe();
      debouncedGeoImpressions.clear();
    };
  }, [debouncedFetch, getValues, updateChannel, watch, debouncedGeoImpressions]);

  // Direct URL to an inaccessible channel (e.g. GAM without an account): sync
  // form/URL to an available one; render against `effectiveChannel` below.
  useEffect(() => {
    if (!currentChannels.length || !currentChannelWatch) return;
    if (!currentChannels.includes(currentChannelWatch)) {
      setValue('currentChannel', currentChannels[0]);
    }
  }, [currentChannels, currentChannelWatch, setValue]);

  const effectiveChannel =
    currentChannelWatch && currentChannels.length && !currentChannels.includes(currentChannelWatch)
      ? currentChannels[0]
      : currentChannelWatch;

  useEffect(() => {
    if (searchParams.get('currentChannel') === currentChannelWatch || !currentChannelWatch) return;
    const searchParamsToSet = { currentChannel: currentChannelWatch };
    const searchId = searchParams.get('id');
    if (searchId) {
      searchParamsToSet.id = searchId;
    }
    setSearchParams(searchParamsToSet);
  }, [currentChannelWatch, searchParams, setSearchParams]);
  //#endregion

  const handlePrint = useReactToPrint({
    documentTitle: getDocumentTitle('Forecaster'),
    content: () => {
      onMenuClose();
      return printContainerRef.current;
    },
  });

  const submit = useCallback(
    data => {
      const dataToSend = mapDataToSend(data, currentChannels);
      dataToSend.onSuccess = navigateToForecasterTable;
      dispatch(postForecast(dataToSend));
    },
    [dispatch, navigateToForecasterTable, currentChannels],
  );

  return (
    <FormProvider {...methods}>
      <form ref={printContainerRef} onSubmit={handleSubmit(submit)}>
        <Box
          sx={{
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            padding: '16px 30px',
            '@media print': {
              height: 'auto',
              padding: '10px',
            },
          }}
          className='w-full space-y-4 overflow-y-auto print:space-y-[10px]'
        >
          <div className='flex items-center print:hidden gap-4'>
            <ForecasterHeader>
              <h2 className='text-[#1E1E1E] text-[24px] font-semibold'>Plan your campaign</h2>
              {isAIAllowed && <AIButton onClick={navigateToForecasterAI} />}
            </ForecasterHeader>
            <div className='flex justify-between'>
              <Button
                id={MENU_TRIGGER_ID}
                startIcon={<OptionsIcon />}
                endIcon={<ArrowDropDown sx={{ color: '#2E2C34' }} />}
                variant='outlined'
                sx={{
                  gap: '4px',
                  marginRight: '20px',
                  bgcolor: 'white',
                  height: '40px',
                  width: '150px',
                }}
                onClick={onMenuOpen}
                aria-controls={menuOpen ? MENU_ID : undefined}
                aria-haspopup='true'
                aria-expanded={menuOpen ? 'true' : undefined}
              >
                <span>Options</span>
              </Button>
              <Menu
                id={MENU_ID}
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={onMenuClose}
                slotProps={{
                  list: {
                    'aria-labelledby': MENU_TRIGGER_ID,
                  },
                }}
              >
                <MenuItem onClick={navigateToForecasterTable}>
                  <Link to={Route.FORECASTER_TABLE}>
                    <span>View Saved Forecasts</span>
                  </Link>
                </MenuItem>
                <MenuItem onClick={handlePrint}>
                  <span>Print PDF Report</span>
                </MenuItem>
              </Menu>
              <Button
                type='submit'
                variant='contained'
                startIcon={<SaveOutlined />}
                sx={{
                  height: '40px',
                  width: '170px',
                  marginLeft: '15px',
                }}
                disabled={!!forecastRequestId}
              >
                <span>Save Forecast</span>
              </Button>
            </div>
          </div>
          <div className='flex gap-4 relative print:flex-col-reverse'>
            <div className='flex-1 print:w-full flex flex-col gap-4'>
              <Geographic currentChannel={effectiveChannel} />

              <Fragment key={`right_controls_${effectiveChannel}`}>
                <Channels />

                <div className='flex flex-col gap-4 relative'>
                  <ControlComponents
                    currentChannel={effectiveChannel}
                    filterVenueTypes={channelsData?.Dooh?.filterVenueTypes}
                  />
                  <Backdrop
                    sx={{
                      position: 'absolute',
                      zIndex: 100,
                      borderRadius: '6px',
                      backgroundColor: 'rgb(0 0 0 / .15)',
                    }}
                    open={currentChannelControlsLocked}
                  >
                    <CircularProgress />
                  </Backdrop>
                </div>
              </Fragment>
            </div>
            <div
              key={`left_controls_${effectiveChannel}`}
              className='flex-0 basis-[350px] flex flex-col gap-4 h-fit sticky top-0 print:static print:w-full'
            >
              <ForecastName />
              <Estimation forecasterData={channelsData} currentChannel={effectiveChannel} />
              <SpendCalculator forecasterData={channelsData} currentChannel={effectiveChannel} />
            </div>
          </div>
        </Box>
      </form>
    </FormProvider>
  );
};

export default Forecaster;
